// The house style from Variant C's hero, for the product pages: a real photograph of the unit in use,
// and the maker's own rating plate with their code engraved into it. BrandedMark stays out of the picture.
// The plate is the shared MakerPlate (ui/brandcode); Variant C's fictional units are read, not copied.
import { useHeroUnit } from '../../heroUnits';
import { cx } from '../../ui';
import { MakerPlate, type PlateSpec } from '../../ui/brandcode';
import { Img } from '../../variants/c/parts';
import { SECTORS, sectorByKey, type SectorKey } from '../../variants/c/sectors';

/** Photo of the unit in its setting, framed, with the maker's plate overlapping the corner.
 *  With no `unit`, it cycles through the lead units (cut, not fade; reduced motion stays on the first). */
export function UnitPlateHero({ unit, className, caption = true }: { unit?: SectorKey; className?: string; caption?: boolean }) {
  const [hu] = useHeroUnit(5200);
  const s = unit ? sectorByKey(unit) : sectorByKey(hu.key);
  // Three rows read at a glance; the full plate is on the homepage.
  const plate: PlateSpec = {
    finish: 'steel',
    maker: s.maker,
    kind: s.kind,
    mark: s.mark,
    accent: s.accent,
    monoInk: s.accentInk,
    rows: s.plate.filter(([k]) => k === 'Model' || k === 'Serial no.' || k === 'Year'),
    serialKey: 'Serial no.',
    meet: `Meet your ${s.fullModel}`,
  };
  return (
    <figure className={cx('uph', className)}>
      <div className="frame uph-frame">
        <Img key={s.key} src={s.scene} fallback={s.sceneFallback} alt={s.sceneAlt} width={1600} height={1067} className="uph-img" />
      </div>
      <div
        className="uph-plate"
        role="img"
        aria-label={`Rating plate of a fictional ${s.kind.toLowerCase()}, ${s.fullModel}, serial ${s.serial}, with ${s.maker}'s code engraved beside the serial`}
      >
        <MakerPlate p={plate} key={s.key} />
      </div>
      {caption && (
        <figcaption className="uph-cap">
          {s.fullModel} · {s.partnerRole === 'installer' ? 'fitted by' : 'looked after by'} {s.partner}
          <span className="uph-cap-note">Fictional brand, for illustration</span>
        </figcaption>
      )}
      <UphStyles />
    </figure>
  );
}

export const LEAD_UNITS = SECTORS.map((s) => s.key);

function UphStyles() {
  return (
    <style>{`
.v2 .uph { position: relative; margin: 0; }
.v2 .uph-frame { overflow: hidden; }
.v2 .uph-img { display: block; width: 100%; aspect-ratio: 4 / 3; object-fit: cover; }
.v2 .uph-plate { position: relative; margin: -132px 0 0 -14px; width: min(440px, 84%); }
.v2 .uph-plate .mp-row b { white-space: nowrap; }
.v2 .uph-cap { margin-top: 16px; display: flex; flex-wrap: wrap; justify-content: space-between; gap: 4px 16px; font-size: 13px; color: var(--bm-muted); }
.v2 .uph-cap-note { color: #8C8AA8; }
@media (max-width: 639px) { .v2 .uph-plate { width: auto; margin: -64px 10px 0; } }
`}</style>
  );
}
